import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import {
  addWeekdayCheckInTime,
  addWeekdayCheckInTimeByDuration,
  addWeekdayCheckOutTime,
  addWeekdayCheckOutTimeByDuration,
  getWeekdayCheckInAndCheckOutTimesDataAsync,
  removeWeekdayCheckInTime,
  removeWeekdayCheckInTimeByDuration,
  removeWeekdayCheckOutTime,
  removeWeekdayCheckOutTimeByDuration,
  selectWeekdayCheckInAndCheckOutTimeItemsDataLoadingStatus,
  selectWeekdayCheckInCheckOutTimeItemsData,
  updateDefaultRateWeekdayCheckInAndCheckOutTimesDataAsync,
  updateWeekdayCheckInTime,
  updateWeekdayCheckOutTime,
} from '../defaultRateWeekdayRulesSlice';
import { ActionButtons } from '../../../common/components';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  WeekdayCheckInAndCheckOutTimeByDurationItem,
  WeekdayCheckInAndCheckOutTimeItem,
} from '../../../common/types/rates.types';
import { WEEKDAYS_SHORT } from '../../../common/constants';
import WeekdayCheckInCheckOutTimesForm from './WeekdayCheckInCheckOutTimesForm';
import { copyWeekdayCheckInCheckOutTimeRulesToSeasons } from '../../defaultRateTools/defaultRateToolsSlice';
import { formatDateToFinnish } from '../../../common/utils/dateUtils';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';

function DefaultRateWeekdayCheckInCheckOutTimes({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const weekdayCheckInCheckOutTimeItemsData = useAppSelector(
    selectWeekdayCheckInCheckOutTimeItemsData,
  );
  const weekdayCheckInCheckOutTimeItemsDataStatus = useAppSelector(
    selectWeekdayCheckInAndCheckOutTimeItemsDataLoadingStatus,
  );

  const initialCheckInTimeData: WeekdayCheckInAndCheckOutTimeItem = {
    weekday: -1,
    timeOfDay: '',
    timesByDuration: [],
    isActive: true,
    isCopyable: true,
  };

  const initialCheckOutTimeData: WeekdayCheckInAndCheckOutTimeItem = {
    weekday: -1,
    timeOfDay: '',
    timesByDuration: [],
    isActive: true,
    isCopyable: true,
  };

  const initialCheckInTimeByDurationData: WeekdayCheckInAndCheckOutTimeByDurationItem =
    {
      weekday: -1,
      timeOfDay: '',
      lengthOfStay: 0,
    };

  const initialCheckOutTimeByDurationData: WeekdayCheckInAndCheckOutTimeByDurationItem =
    {
      weekday: -1,
      timeOfDay: '',
      lengthOfStay: 0,
    };

  const weekdayOptions = WEEKDAYS_SHORT.map(item => {
    return {
      value: item.weekdayInt,
      label: item.weekdayString,
    };
  });

  const [selectedWeekday, setSelectedWeekday] = useState(-1);
  const [checkInTimeData, setCheckInTimeData] = useState(
    initialCheckInTimeData,
  );
  const [checkOutTimeData, setCheckOutTimeData] = useState(
    initialCheckOutTimeData,
  );
  const [checkInTimeByDurationData, setCheckInTimeByDurationData] = useState(
    initialCheckInTimeByDurationData,
  );
  const [checkOutTimeByDurationData, setCheckOutTimeByDurationData] = useState(
    initialCheckOutTimeByDurationData,
  );
  const [submitted, setSubmitted] = useState(false);
  const [isExistingCheckInTime, setIsExistingCheckInTime] = useState(false);
  const [isExistingCheckOutTime, setIsExistingCheckOutTime] = useState(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'' | 'checkin' | 'checkout'>('');

  const [copyStartDate, setCopyStartDate] = useState<Date | null>(null);
  const [copyEndDate, setCopyEndDate] = useState<Date | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  const handleModalStatus = (
    visibility: boolean,
    formType: '' | 'checkin' | 'checkout',
  ) => {
    setShowModal(visibility);
    setModalType(formType);
  };

  useEffect(() => {
    dispatch(getWeekdayCheckInAndCheckOutTimesDataAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  const handleCheckInTimeInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newCheckInTimeData = { ...checkInTimeData };
    let pointer = newCheckInTimeData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setCheckInTimeData(newCheckInTimeData);
  };

  const handleCheckInTimeByDurationInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newCheckInTimeByDurationData = { ...checkInTimeByDurationData };
    let pointer = newCheckInTimeByDurationData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setCheckInTimeByDurationData(newCheckInTimeByDurationData);
  };

  const handleCheckOutTimeInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newCheckOutTimeData = { ...checkOutTimeData };
    let pointer = newCheckOutTimeData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setCheckOutTimeData(newCheckOutTimeData);
  };

  const handleCheckOutTimeByDurationInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newCheckOutTimeByDurationData = { ...checkOutTimeByDurationData };
    let pointer = newCheckOutTimeByDurationData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setCheckOutTimeByDurationData(newCheckOutTimeByDurationData);
  };

  const handleWeekdayChange = selectedOption => {
    setSelectedWeekday(selectedOption.value);

    const existingWeekdayCheckInTime =
      weekdayCheckInCheckOutTimeItemsData?.checkInTimes?.find(
        rule => rule?.weekday === selectedOption.value,
      );

    if (existingWeekdayCheckInTime && existingWeekdayCheckInTime.weekday) {
      setCheckInTimeData(existingWeekdayCheckInTime);
      setIsExistingCheckInTime(true);
    } else {
      setCheckInTimeData({
        ...initialCheckInTimeData,
        weekday: selectedOption.value,
      });
      setIsExistingCheckInTime(false);
    }

    const existingWeekdayCheckOutTime =
      weekdayCheckInCheckOutTimeItemsData?.checkOutTimes?.find(
        rule => rule?.weekday === selectedOption.value,
      );

    if (existingWeekdayCheckOutTime && existingWeekdayCheckOutTime.weekday) {
      setCheckOutTimeData(existingWeekdayCheckOutTime);
      setIsExistingCheckOutTime(true);
    } else {
      setCheckOutTimeData({
        ...initialCheckOutTimeData,
        weekday: selectedOption.value,
      });
      setIsExistingCheckOutTime(false);
    }
  };

  const addOrUpdateCheckInTimeRule = () => {
    const existingWeekdayCheckInTime =
      weekdayCheckInCheckOutTimeItemsData?.checkInTimes?.find(
        rule => rule?.weekday === checkInTimeData.weekday,
      );

    if (!existingWeekdayCheckInTime) {
      dispatch(addWeekdayCheckInTime(checkInTimeData));
    } else {
      dispatch(updateWeekdayCheckInTime(checkInTimeData));
    }

    handleModalStatus(false, '');
    setSelectedWeekday(-1);
  };

  const addOrUpdateCheckOutTimeRule = () => {
    const existingWeekdayCheckOutTime =
      weekdayCheckInCheckOutTimeItemsData?.checkOutTimes?.find(
        rule => rule?.weekday === checkOutTimeData.weekday,
      );

    if (!existingWeekdayCheckOutTime) {
      dispatch(addWeekdayCheckOutTime(checkOutTimeData));
    } else {
      dispatch(updateWeekdayCheckOutTime(checkOutTimeData));
    }

    handleModalStatus(false, '');
    setSelectedWeekday(-1);
  };

  const addCheckInTimeByDurationRule = () => {
    dispatch(
      addWeekdayCheckInTimeByDuration({
        weekday: selectedWeekday,
        data: checkInTimeByDurationData,
      }),
    );
  };

  const addCheckOutTimeByDurationRule = () => {
    dispatch(
      addWeekdayCheckOutTimeByDuration({
        weekday: selectedWeekday,
        data: checkOutTimeByDurationData,
      }),
    );
  };

  const removeCheckInTimeRule = () => {
    const existingWeekdayCheckInTime =
      weekdayCheckInCheckOutTimeItemsData?.checkInTimes?.find(
        rule => rule?.weekday === checkInTimeData.weekday,
      );

    if (existingWeekdayCheckInTime) {
      dispatch(removeWeekdayCheckInTime(existingWeekdayCheckInTime.weekday));
    }

    handleModalStatus(false, '');
    setSelectedWeekday(-1);
  };

  const removeCheckInTimeByDurationRule = (id, weekday) => {
    dispatch(removeWeekdayCheckInTimeByDuration({ id, weekday }));
  };

  const removeCheckOutTimeByDurationRule = (id, weekday) => {
    dispatch(removeWeekdayCheckOutTimeByDuration({ id, weekday }));
  };

  const removeCheckOutTimeRule = () => {
    const existingWeekdayCheckOutTime =
      weekdayCheckInCheckOutTimeItemsData?.checkOutTimes?.find(
        rule => rule?.weekday === checkOutTimeData.weekday,
      );

    if (existingWeekdayCheckOutTime) {
      dispatch(removeWeekdayCheckOutTime(existingWeekdayCheckOutTime.weekday));
    }

    handleModalStatus(false, '');
    setSelectedWeekday(-1);
  };

  const onSubmit = () => {
    dispatch(
      updateDefaultRateWeekdayCheckInAndCheckOutTimesDataAsync(
        weekdayCheckInCheckOutTimeItemsData,
      ),
    );
    setSubmitted(true);
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyWeekdayCheckInCheckOutTimeRulesToSeasons({
          accommodationId,
          // copyStartDate: formatDateToFinnish(copyStartDate),
          // copyEndDate: formatDateToFinnish(copyEndDate),
          selectedSeasonRateIds,
          checkInTimes: weekdayCheckInCheckOutTimeItemsData?.checkInTimes,
          checkOutTimes: weekdayCheckInCheckOutTimeItemsData?.checkOutTimes,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  const renderCurrentRules = (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <div className="d-flex align-items-start mb-24">
        <h4 className="h5 h-md-4 flex-grow-1 me-16">Sisäänkirjautumisajat</h4>
        {hasWriteAccess && (
          <Button
            size="sm"
            variant="blue"
            onClick={() => handleModalStatus(true, 'checkin')}
          >
            Lisää / Muokkaa
          </Button>
        )}
      </div>
      <div className="table-responsive-lg mb-48">
        <Table bordered>
          <thead>
            <tr>
              <th>Viikonpäivä</th>
              <th>Kellonaika</th>
              <th>Kopioitava</th>
              <th>Aktiivinen</th>
              <th>Sisäänkirjautumisen kellonajat varauksen keston mukaan</th>
            </tr>
          </thead>
          <tbody>
            {weekdayCheckInCheckOutTimeItemsData.checkInTimes &&
              weekdayCheckInCheckOutTimeItemsData.checkInTimes.length < 1 && (
                <tr>
                  <td className="p-24 text-muted" colSpan={7}>
                    Ei lisättyjä sisäänkirjautumisaikoja
                  </td>
                </tr>
              )}
            {WEEKDAYS_SHORT.map(weekdayItem => {
              const existingCheckInTime =
                weekdayCheckInCheckOutTimeItemsData?.checkInTimes?.find(
                  rule => rule?.weekday === weekdayItem.weekdayInt,
                );

              if (!existingCheckInTime) {
                return null;
              }

              return (
                <tr key={`weekdayCheckInTime-${weekdayItem.weekdayInt}`}>
                  <td>
                    <strong>{weekdayItem.weekdayString}</strong>
                  </td>
                  <td>{existingCheckInTime.timeOfDay}</td>
                  <td>{existingCheckInTime.isCopyable ? 'Kyllä' : 'Ei'}</td>
                  <td>{existingCheckInTime.isActive ? 'Kyllä' : 'Ei'}</td>
                  <td>
                    {existingCheckInTime.timesByDuration.map(item => {
                      return (
                        <span
                          className="align-items-start d-flex mb-8"
                          key={item.id}
                        >
                          <span className="d-block flex-grow-1 me-8">
                            Kesto: {item.lengthOfStay}
                            <br />
                            Kellonaika: {item.timeOfDay}
                          </span>
                          {hasWriteAccess && (
                            <Button
                              variant="outline-danger"
                              className="px-8"
                              size="sm"
                              onClick={() =>
                                removeCheckInTimeByDurationRule(
                                  item.id,
                                  existingCheckInTime.weekday,
                                )
                              }
                              style={{ height: 36, width: 36 }}
                            >
                              x
                            </Button>
                          )}
                        </span>
                      );
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <hr className="hr-light mb-32" />

      <div className="d-flex align-items-start mb-24">
        <h4 className="h5 h-md-4 flex-grow-1 me-16">Uloskirjautumisajat</h4>
        {hasWriteAccess && (
          <Button
            size="sm"
            variant="blue"
            onClick={() => handleModalStatus(true, 'checkout')}
          >
            Lisää / Muokkaa
          </Button>
        )}
      </div>
      <div className="table-responsive-lg mb-48">
        <Table bordered>
          <thead>
            <tr>
              <th>Viikonpäivä</th>
              <th>Kellonaika</th>
              <th>Kopioitava</th>
              <th>Aktiivinen</th>
              <th>Uloskirjautumisen kellonajat varauksen keston mukaan</th>
            </tr>
          </thead>
          <tbody>
            {weekdayCheckInCheckOutTimeItemsData.checkOutTimes &&
              weekdayCheckInCheckOutTimeItemsData.checkOutTimes.length < 1 && (
                <tr>
                  <td className="p-24 text-muted" colSpan={7}>
                    Ei lisättyjä uloskirjautumisaikoja
                  </td>
                </tr>
              )}
            {WEEKDAYS_SHORT.map(weekdayItem => {
              const existingCheckOutTime =
                weekdayCheckInCheckOutTimeItemsData?.checkOutTimes?.find(
                  rule => rule?.weekday === weekdayItem.weekdayInt,
                );

              if (!existingCheckOutTime) {
                return null;
              }

              return (
                <tr key={`weekdayCheckOutTime-${weekdayItem.weekdayInt}`}>
                  <td>
                    <strong>{weekdayItem.weekdayString}</strong>
                  </td>
                  <td>{existingCheckOutTime.timeOfDay}</td>
                  <td>{existingCheckOutTime.isCopyable ? 'Kyllä' : 'Ei'}</td>
                  <td>{existingCheckOutTime.isActive ? 'Kyllä' : 'Ei'}</td>
                  <td>
                    {existingCheckOutTime.timesByDuration.map(item => {
                      return (
                        <span
                          className="align-items-start d-flex mb-8"
                          key={item.id}
                        >
                          <span className="d-block flex-grow-1 me-8">
                            Kesto: {item.lengthOfStay}
                            <br />
                            Kellonaika: {item.timeOfDay}
                          </span>
                          {hasWriteAccess && (
                            <Button
                              variant="outline-danger"
                              className="px-8"
                              size="sm"
                              onClick={() =>
                                removeCheckOutTimeByDurationRule(
                                  item.id,
                                  existingCheckOutTime.weekday,
                                )
                              }
                              style={{ height: 36, width: 36 }}
                            >
                              x
                            </Button>
                          )}
                        </span>
                      );
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );

  return (
    <>
      {hasWriteAccess && (
        <Modal
          size="lg"
          show={showModal}
          backdrop="static"
          keyboard={false}
          onHide={() => handleModalStatus(false, '')}
        >
          <Modal.Header closeButton>
            <Modal.Title>Lisää / muokkaa sisäänkirjautumisaika</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-24">
              <Form.Label>Viikonpäivä</Form.Label>
              <Select
                inputId="weekday"
                name="weekday"
                placeholder="Valitse viikonpäivä"
                isClearable
                options={weekdayOptions}
                onChange={option => handleWeekdayChange(option)}
                value={weekdayOptions.filter(
                  option => option.value === selectedWeekday,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            {selectedWeekday > -1 && hasWriteAccess && (
              <WeekdayCheckInCheckOutTimesForm
                addOrUpdateCheckInTimeRule={addOrUpdateCheckInTimeRule}
                addOrUpdateCheckOutTimeRule={addOrUpdateCheckOutTimeRule}
                addCheckInTimeByDurationRule={addCheckInTimeByDurationRule}
                addCheckOutTimeByDurationRule={addCheckOutTimeByDurationRule}
                removeCheckInTimeRule={removeCheckInTimeRule}
                removeCheckOutTimeRule={removeCheckOutTimeRule}
                handleCheckInTimeInputChange={handleCheckInTimeInputChange}
                handleCheckOutTimeInputChange={handleCheckOutTimeInputChange}
                handleCheckInTimeByDurationInputChange={
                  handleCheckInTimeByDurationInputChange
                }
                handleCheckOutTimeByDurationInputChange={
                  handleCheckOutTimeByDurationInputChange
                }
                checkInTimeData={checkInTimeData}
                checkOutTimeData={checkOutTimeData}
                checkInTimeByDurationData={checkInTimeByDurationData}
                checkOutTimeByDurationData={checkOutTimeByDurationData}
                isExistingCheckInTime={isExistingCheckInTime}
                isExistingCheckOutTime={isExistingCheckOutTime}
                formToDisplay={modalType}
                isDefaultRate
              />
            )}
          </Modal.Body>
        </Modal>
      )}

      {renderCurrentRules}

      <ActionButtons
        HandleSave={onSubmit}
        HideSaveButton={!hasWriteAccess}
        SaveButtonText="Tallenna tehdyt muutokset"
        HideCancelButton
        DeleteButtonText=""
      />
      {submitted && (
        <div className="mt-2 pt-2">
          <Alert variant="success">Tiedot tallennettu!</Alert>
        </div>
      )}
      <Form.Group className="mb-24">
        {/*
        <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyStartDate(date)}
            className="form-control"
            selected={copyStartDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyEndDate(date)}
            className="form-control"
            selected={copyEndDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Text muted>
          Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
          päättymispäivämäärää sekä loppupäivä on kopioinnin alkamispäivämäärän
          jälkeen.
        </Form.Text>
        */}
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateWeekdayCheckInCheckOutTimes;
